import { DateTime } from "luxon";
import { z } from "zod";
import { fromError, isValidationError } from "zod-validation-error";
function validateData(data, schema) {
  const parsedResult = schema.safeParse(data);
  if (parsedResult.success) {
    return parsedResult.data;
  }
  throw fromError(parsedResult.error);
}
function dataRespectsSchema(data, schema) {
  return schema.safeParse(data).success;
}
function safeParseJSONWithSchema(jsonString, schema) {
  try {
    const parsed = JSON.parse(jsonString);
    try {
      return validateData(parsed, schema);
    } catch (error) {
      console.error(
        "Error validating JSON after parsing",
        isValidationError(error) ? error.toString() : error
      );
      return null;
    }
  } catch (error) {
    console.error("Error parsing JSON", error);
    return null;
  }
}
function parseJSONWithSchema(jsonString, schema) {
  const parsed = JSON.parse(jsonString);
  return validateData(parsed, schema);
}
function millisToDateTimeTransform(options) {
  return (timestamp, ctx) => {
    const dateTime = DateTime.fromMillis(timestamp, options);
    if (!dateTime.isValid) {
      ctx.addIssue({
        code: "custom",
        message: `${timestamp} is not a valid timestamp: ${dateTime.invalidReason}`
      });
      return z.NEVER;
    }
    return dateTime;
  };
}
function zodTypeToBlueprint(schema, indent) {
  const pad = "  ".repeat(indent);
  const def = schema._def;
  const type = def.type;
  switch (type) {
    case "string":
      return "string";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "null":
      return "null";
    case "literal": {
      const values = def.values;
      return values.map((v) => typeof v === "string" ? `"${v}"` : String(v)).join(" | ");
    }
    case "enum": {
      const entries = def.entries;
      return Object.values(entries).map((v) => `"${v}"`).join(" | ");
    }
    case "array": {
      const elementType = zodTypeToBlueprint(
        def.element,
        indent
      );
      return elementType.includes(" ") ? `(${elementType})[]` : `${elementType}[]`;
    }
    case "optional": {
      const inner = zodTypeToBlueprint(
        def.innerType,
        indent
      );
      return `${inner} | undefined`;
    }
    case "nullable": {
      const inner = zodTypeToBlueprint(
        def.innerType,
        indent
      );
      return `${inner} | null`;
    }
    case "union": {
      const options = def.options;
      return options.map((o) => zodTypeToBlueprint(o, indent)).join(" | ");
    }
    case "object": {
      const shape = def.shape;
      const innerPad = "  ".repeat(indent + 1);
      const fields = Object.entries(shape).map(([key, fieldSchema]) => {
        const fieldDef = fieldSchema._def;
        const isOptional = fieldDef.type === "optional";
        const description = fieldSchema.description;
        const comment = description ? `${innerPad}// ${description}
` : "";
        const optMark = isOptional ? "?" : "";
        const fieldType = zodTypeToBlueprint(fieldSchema, indent + 1);
        const displayType = isOptional ? fieldType.replace(/\s*\|\s*undefined$/, "") : fieldType;
        return `${comment}${innerPad}${key}${optMark}: ${displayType};`;
      });
      return `{
${fields.join("\n")}
${pad}}`;
    }
    default:
      return "unknown";
  }
}
function buildSchemaPrompt(schema) {
  return `Respond with JSON (no whitespace outside string literals):
${zodTypeToBlueprint(schema, 0)}`;
}
function stringToJsonTransform(schema) {
  return (json, ctx) => {
    let blob;
    try {
      blob = JSON.parse(json);
    } catch (error) {
      ctx.addIssue({
        code: "custom",
        message: `Invalid JSON string: ${String(error)}`
      });
      return z.NEVER;
    }
    const result = schema.safeParse(blob);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: "custom",
          message: issue.message,
          path: issue.path
        });
      }
      return z.NEVER;
    }
    return result.data;
  };
}
export {
  buildSchemaPrompt,
  dataRespectsSchema,
  millisToDateTimeTransform,
  parseJSONWithSchema,
  safeParseJSONWithSchema,
  stringToJsonTransform,
  validateData
};
//# sourceMappingURL=zodHelpers.js.map
